import { module } from 'modujs';
import { isDebug } from '../utils/environment';

const DEBUG = false; // isDebug

export const STATE = {
    IDLE:       'IDLE',       // Form is ready
    PROCESSING: 'PROCESSING', // Form is being processed
    TESTING:    'TESTING',    // Form is being tested
    SENDING:    'SENDING',    // Form is being sent and awaiting response
    INVALID:    'INVALID',    // Form is invalid (HTTP 400)
    ERRORED:    'ERRORED',    // Form can not be processed (HTTP 500)
    COMPLETED:  'COMPLETED',  // Form was processed successfully (HTTP 201)
}

export const CLASS = {
    IS_BUSY:       'is-busy',
    IS_LOADING:    'is-loading',
    IS_TESTING:    'is-testing',
    IS_SENDING:    'is-sending',
    IS_SUCCESS:    'is-success',
    IS_INVALID:    'is-invalid',
    HAS_ERROR:     'has-error',
    WAS_VALIDATED: 'was-validated',
}

/**
 * Form Submission
 */
export default class extends module {
    constructor(m) {
        super(m)

        this.events = {
            'recaptcha-executed': 'onRecaptcha',
            'submit':             'onSubmit',
        }

        this.submitEvent = null

        this.state = STATE.IDLE
        this.busy  = false
    }

    init() {
        DEBUG && console.log('[App.Form] Initializing…')

        this.onInvalid = this.onInvalid.bind(this)

        this.el.addEventListener('invalid', this.onInvalid, {
            capture: true,
            passive: true,
        });

        this.recaptchaModule;
        this.formAction      = this.getData('action')
        this.confirmMessage  = this.getData('confirm-message')
        this.warningMessage  = this.getData('warning-message')
        this.errorMessage    = this.getData('error-message')
        this.criticalMessage = this.getData('critical-message')
    }

    destroy() {
        this.el.removeEventListener('invalid', this.onInvalid, {
            capture: true,
        });
    }

    /**
     * Change the current state of the form.
     *
     * @param {String} state - The form state.
     */
    setState(state) {
        // Always assume the form is busy
        this.busy  = true
        this.state = STATE.PROCESSING

        this.el.classList.add(CLASS.IS_BUSY)
        this.lockForm()

        // And assume we are idle
        this.el.classList.remove(CLASS.IS_LOADING)
        this.el.classList.remove(CLASS.IS_TESTING)
        this.el.classList.remove(CLASS.IS_SENDING)
        this.el.classList.remove(CLASS.IS_SUCCESS)
        this.el.classList.remove(CLASS.IS_INVALID)
        this.el.classList.remove(CLASS.HAS_ERROR)
        this.el.classList.remove(CLASS.WAS_VALIDATED)

        switch (state) {
            case STATE.IDLE:
                this.busy  = false
                this.state = state

                this.el.classList.remove(CLASS.IS_BUSY)

                this.setFeedback()
                this.unlockForm()
                break

            case STATE.PROCESSING:
                this.state = state
                break

            case STATE.TESTING:
                this.state = state

                this.el.classList.add(CLASS.IS_TESTING)
                break

            case STATE.SENDING:
                this.state = state

                this.el.classList.add(CLASS.IS_SENDING)
                break

            case STATE.COMPLETED:
                this.busy  = false
                this.state = state

                this.el.classList.remove(CLASS.IS_BUSY)
                this.el.classList.add(CLASS.IS_SUCCESS)

                this.el.reset()
                this.unlockForm()
                break

            case STATE.INVALID:
                this.busy  = false
                this.state = state

                this.el.classList.remove(CLASS.IS_BUSY)
                this.el.classList.add(CLASS.IS_INVALID)

                this.unlockForm()
                break

            case STATE.ERRORED:
                this.busy  = false
                this.state = state

                this.el.classList.remove(CLASS.IS_BUSY)
                this.el.classList.add(CLASS.HAS_ERROR)

                this.setFeedback(this.errorMessage)
                this.disableFormFields()
                break

            default:
                this.busy  = false
                this.state = STATE.ERRORED

                this.el.classList.remove(CLASS.IS_BUSY)
                this.el.classList.add(CLASS.HAS_ERROR)

                this.setFeedback(this.criticalMessage)
                this.disableFormFields()
                break
        }
    }

    /**
     * @param {String} [message] Leave blank to clear existing message.
     */
    setFeedback(message) {
        if (!message) {
            message = ''
        }

        try {
            const feedback = this.$('feedback')[0]

            feedback.innerHTML = message

            this.updateScroll()

            if (message) {
                this.call('scrollTo', { target: feedback }, 'Scroll')
            }
        } catch (error) {
            console.error('[App.Form.setFeedback]', error)
            this.setState(STATE.ERRORED)
            this.setFeedback(this.errorMessage)
        }
    }

    /**
     * @param  {String} state - The form state.
     * @return {Boolean}
     */
    isState(state) {
       return this.state === state
    }

    /**
     * @return {Boolean}
     */
    isFormBusy() {
       return this.el.classList.contains(CLASS.IS_BUSY)
    }

    /**
     * @return {Boolean}
     */
    isFormSending() {
       return this.el.classList.contains(CLASS.IS_SENDING)
    }

    /**
     * @return {Boolean}
     */
    isFormLocked() {
        return !! this.el.disabled
    }

    /**
     * @return {Boolean}
     */
    wasFormValidated() {
        return this.el.classList.contains(CLASS.WAS_VALIDATED)
    }

    canElemReadOnly(elem) {
        switch (elem.type) {
            case 'button':
            case 'checkbox':
            case 'radio':
            case 'reset':
            case 'select-one':
            case 'select-multiple':
            case 'submit':
                return false
        }

        return true
    }

    /**
     * Lock form.
     */
    lockForm() {
        this.el.setAttribute('disabled', true)
        this.el.disabled = true
    }

    /**
     * Unlock form.
     */
    unlockForm() {
        this.el.removeAttribute('disabled')
        this.el.disabled = false
    }

    /**
     * Disable edition of form fields.
     */
    disableFormFields() {
        this.el.setAttribute('disabled', true)
        this.el.disabled = true

        Array.from(this.el.elements).forEach((elem) => {
            elem.setAttribute('disabled', 'disabled')
            elem.disabled = true
        })
    }

    /**
     * Enable edition of form fields.
     */
    enableFormFields() {
        Array.from(this.el.elements).forEach((elem) => {
            elem.removeAttribute('disabled')
            elem.disabled = false
        })
    }

    /**
     * Listens to the input "invalid" event.
     *
     * @param {Event} event - The invalid event.
     */
    onInvalid(event) {
        this.el.classList.add(CLASS.WAS_VALIDATED)
    }

    /**
     * Listens to the "recaptcha-executed" event.
     *
     * Proxy handler to trigger the form submission.
     *
     * @param {Event} event - The recaptcha-executed event.
     */
    onRecaptcha(event) {
        DEBUG && console.log('[App.Form.onRecaptcha] Handling "recaptcha-executed" event:', event)
        if (event.detail) {
            this.submitForm()
        }
    }

    /**
     * Listens to the form "submit" event.
     *
     * Proxy handler to trigger the CAPTCHA, if present.
     *
     * @param {Event} event - The submit event.
     */
    onSubmit(event) {
        DEBUG && console.log('[App.Form.onSubmit] Handling "submit" event:', event)
        event.preventDefault()

        if (this.isFormBusy() || this.isFormLocked()) {
            console.warn('Form is busy or locked')
            return false
        }

        this.el.classList.add(CLASS.WAS_VALIDATED)
        if (event.target.checkValidity() === false) {
            isDebug && console.log('Form is not valid')
            return false
        }

        this.submitEvent = event

        this.setFeedback()
        this.setState(STATE.PROCESSING)

        try {
            const recaptchaContainer = this.getRecaptchaContainerField()
            if (recaptchaContainer && recaptchaContainer.getAttribute('data-size') === 'invisible') {
                this.setState(STATE.TESTING)

                // Check for the verified invisible reCAPTCHA token first.
                const response = grecaptcha.getResponse(recaptchaContainer.getAttribute('data-widget-id'))
                if (!response) {
                    // Execute the invisible reCAPTCHA.
                    grecaptcha.execute(recaptchaContainer.getAttribute('data-widget-id'))
                }
            } else {
                this.setState(STATE.TESTING)

                this.submitForm()
            }
        } catch (error) {
            console.error('[App.Form.onSubmit]', error)
            this.setState(STATE.ERRORED)
            this.setFeedback(this.errorMessage)
        }
    }

    submitForm() {
        isDebug && console.log('[App.Form.submitForm] Handling submission:', arguments)

        if (this.isFormSending()) {
            console.warn('Form is already sending')
            return false
        }

        if (!this.submitEvent) {
            throw new Error('Missing submit event object')
        }

        this.setState(STATE.SENDING)

        let isRedirected  = false
        let isSuccessful  = false
        let isClientError = false
        let isServerError = false

        const event      = this.submitEvent
        const form       = event.target
        const formUrl    = this.formAction || form.action
        const formData   = new FormData(form)
        const controller = new AbortController()

        fetch(formUrl, {
            method: form.method,
            body:   formData,
            signal: controller.signal
        }).then(response => {
            if (response.url && response.url != formUrl) {
                isRedirected = true
                isSuccessful = true

                try {
                    DEBUG && console.log('[App.Form.submitForm] Soft redirecting page…')
                    this.call('goTo', { url: response.url }, 'Load')
                } catch (e) {
                    DEBUG && console.log('[App.Form.submitForm] Hard redirecting page…')
                    window.location.href = response.url
                }

                controller.abort()

                /**
                 * In case window.location.href and AbortController fail,
                 * provide a dummy response object for the following thenable.
                 */
                return {
                    success: true,
                    retry: false,
                }
            } else if (response.status >= 200 && response.status <= 299) {
                isSuccessful = true
            } else if (response.status >= 400 && response.status <= 499) {
                isClientError = true
            } else if (response.status >= 500 && response.status <= 599) {
                isServerError = true
            }

            return response.json()
        }).then(response => {
            this.resetRecaptcha()

            DEBUG && console.log({ isClientError, isServerError, response })

            // Check for false-positives
            if (isSuccessful && response.success === false) {
                isSuccessful = false

                if (response.retry) {
                    isClientError = true
                } else {
                    isServerError = false
                }
            }

            if (isClientError) {
                this.setState(STATE.INVALID)

                if (response.errors) {
                    const formValidation = this.getFormValidationModule()
                    if (formValidation) {
                        /** Delaying validity to allow the scrollTo of {@see this.setFormState()} to complete. */
                        window.setTimeout(() => {
                            formValidation.reportCustomValidityOnElementsByName(response.errors)
                            formValidation.focusFirstInvalid()
                        }, 900);
                    }
                }

                this.setFeedback(response.message || this.warningMessage)
            } else if (isServerError) {
                this.setState(STATE.ERRORED)
                this.setFeedback(response.message || this.errorMessage)
            } else {
                this.setState(STATE.COMPLETED)
                this.setFeedback(response.message || this.confirmMessage)

                if (!isRedirected && !response.skipReload) {
                    try {
                        DEBUG && console.log('[App.Form.submitForm] Soft reloading page…')
                        const url = new URL(window.location.href)
                        url.searchParams.set('completed', 1)
                        this.call('refresh', { url }, 'Load')
                    } catch (e) {
                        DEBUG && console.log('[App.Form.submitForm] Hard reloading page…')
                        window.location.reload()
                    }
                }
            }
        }).catch(error => {
            if (isRedirected) {
                this.setState(STATE.COMPLETED)
                this.setFeedback(response.message || this.confirmMessage)
            } else {
                console.error('[App.Form.submitForm]', error)
                this.setState(STATE.ERRORED)
                this.setFeedback(this.criticalMessage)
            }
        })
    }

    /**
     * In case of use of locomotive-scroll, we need to update page height
     */
    updateScroll() {
        requestAnimationFrame(() => {
            this.call('update', 'Scroll')
        })
    }

    /**
     * Retrieves the HTML element to render the reCAPTCHA widget.
     *
     * @return {?HTMLElement}
     */
    getRecaptchaContainerField() {
        const mod = this.getRecaptchaModule()
        if (mod) {
            return mod.getContainerElement()
        }

        return null
    }

    /**
     * Retrieves the HTML element that stores the response for the reCAPTCHA widget.
     *
     * @return {?HTMLTextAreaElement}
     */
    getRecaptchaResponseField() {
        const mod = this.getRecaptchaModule()
        if (mod) {
            return mod.getResponseElement()
        }

        return null
    }

    /**
     * Reset the reCAPTCHA element.
     */
    resetRecaptcha() {
        const mod = this.getRecaptchaModule()
        if (mod) {
            mod.reset()
        }
    }

    /**
     * @return {?Recaptcha}
     */
    getRecaptchaModule() {
        if (typeof this.recaptchaModule === 'undefined') {
            this.recaptchaModule = null

            if (this.modules) {
                const mod = 'Recaptcha'

                if (this.modules[mod]) {
                    const id = this.el.getAttribute('data-module-recaptcha')

                    if (id && this.modules[mod][id]) {
                        this.recaptchaModule = this.modules[mod][id]
                    }
                }
            }
        }

        return this.recaptchaModule
    }

    /**
     * @return {?FormValidation}
     */
    getFormValidationModule() {
        if (typeof this.formValidationModule === 'undefined') {
            this.formValidationModule = null;

            if (this.modules) {
                const mod = 'FormValidation';

                if (this.modules[mod]) {
                    const id = this.el.getAttribute('data-module-form-validation');

                    if (id && this.modules[mod][id]) {
                        this.formValidationModule = this.modules[mod][id];
                    }
                }
            }
        }

        return this.formValidationModule;
    }
}
